import { get } from "lodash";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

import LoadingSpinner from "../../components/LoadingSpinner";
import ComparisonTable from "../../components/internal/ComparisonTable";
import ConfigPathSelector from "../../components/internal/ConfigPathSelector";
import PropagationModal from "../../components/internal/PropagationModal";
import {
  batchUpdateClients,
  getDestinationClientsWithConfigs,
} from "../../lib/automatic";

import type { ClientConfig, ClientWithConfig } from "@platform/dmv-api";
import { getErrorMessage } from "../../lib/utility";

type BatchUpdateClientsResult = {
  updated?: { id: string }[];
  failed?: unknown[];
};

/**
 * Main config comparison view for internal employees
 *
 * Layout: narrow left panel (config tree) + wide right panel (comparison table).
 * Flow:
 *  1. Load all destination clients with configs for the given platform client.
 *  2. Search / filter destination clients in the header.
 *  3. User picks a config path in the left panel.
 *  4. Table shows each destination client's current value and diff status vs the model.
 *  5. User pins a model client and selects targets for propagation.
 *  6. PropagationModal previews the diff and confirms the PATCH /clients batch update.
 */
const ClientConfigComparison = () => {
  const { platformClientId } = useParams<{ platformClientId: string }>();
  const navigate = useNavigate();

  const [clients, setClients] = useState<ClientWithConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPath, setSelectedPath] = useState("");
  const [modelClientId, setModelClientId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [inspectingClient, setInspectingClient] =
    useState<ClientWithConfig | null>(null);

  // Load destination clients on mount
  useEffect(() => {
    if (!platformClientId) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data: ClientWithConfig[] = await getDestinationClientsWithConfigs(platformClientId) as ClientWithConfig[];
        // Keep platform client pinned first; sort the rest alphabetically
        const platformEntry = (data ?? []).find((c: ClientWithConfig) => c.isPlatformClient);
        const destEntries = (data ?? [])
          .filter((c) => !c.isPlatformClient)
          .sort((a, b) =>
            (a.displayName ?? "").localeCompare(b.displayName ?? "")
          );
        const ordered = platformEntry
          ? [platformEntry, ...destEntries]
          : destEntries;
        setClients(ordered);
        // Default model to the platform client, falling back to the first entry
        setModelClientId(platformEntry?.id ?? ordered[0]?.id ?? null);
      } catch (err) {
        setError(
          getErrorMessage(err, "Failed to load destination clients")
        );
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [platformClientId]);

  // Filter destination clients by search query (name or ID)
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(
      (c) =>
        c.displayName?.toLowerCase().includes(q) ||
        c.id?.toLowerCase().includes(q)
    );
  }, [clients, searchQuery]);

  // Representative config for the tree (use the model client's config, or first available)
  const representativeConfig = useMemo(() => {
    const model = clients.find((c) => c.id === modelClientId);
    return model?.config ?? clients[0]?.config ?? null;
  }, [clients, modelClientId]);

  const modelClient = useMemo(
    () => clients.find((c) => c.id === modelClientId),
    [clients, modelClientId]
  );

  const modelValue = useMemo(
    () =>
      modelClient && selectedPath
        ? get(modelClient.config, selectedPath)
        : undefined,
    [modelClient, selectedPath]
  );

  const selectedClients = useMemo(
    () => clients.filter((c) => selectedIds.has(c.id)),
    [clients, selectedIds]
  );

  // --- handlers ---

  const handleSetModel = (clientId: string) => {
    setModelClientId(clientId);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(clientId);
      return next;
    });
  };

  const handleToggleSelect = (clientId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const handleSelectAllDiffering = () => {
    if (!selectedPath || !modelClient) return;
    const modelVal = get(modelClient.config, selectedPath);
    const differingIds = filteredClients
      .filter(
        (c) =>
          c.id !== modelClientId &&
          JSON.stringify(get(c.config, selectedPath)) !==
            JSON.stringify(modelVal)
      )
      .map((c) => c.id);
    setSelectedIds(new Set(differingIds));
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handlePropagate = async (patches: Partial<ClientWithConfig>[]) => {
    setIsSubmitting(true);
    try {
      const result = (await batchUpdateClients(
        patches as { id: string; config: ClientConfig }[]
      )) as BatchUpdateClientsResult;
      const updatedCount = result?.updated?.length ?? patches.length;
      const failedCount = result?.failed?.length ?? 0;

      if (failedCount > 0) {
        toast.error(`${failedCount} client(s) failed to update.`);
      } else {
        toast.success(`Updated ${updatedCount} client(s) successfully.`);
      }

      setClients((prev) =>
        prev.map((c) => {
          const patch = patches.find((p) => p.id === c.id);
          if (!patch) return c;
          return { ...c, config: patch.config };
        })
      );
      setSelectedIds(new Set());
      setShowModal(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Propagation failed."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Page header */}
      <div className="flex items-center gap-x-4 px-6 py-4 border-b border-gray-200 shrink-0">
        <button
          onClick={() => navigate("/internal/clients")}
          className="text-sm text-gray-500 hover:text-black"
        >
          &larr; Back
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold truncate">{platformClientId}</h1>
          {!loading && (
            <p className="text-xs text-gray-500">
              {(() => {
                const destTotal = clients.filter(
                  (c) => !c.isPlatformClient
                ).length;
                const destVisible = filteredClients.filter(
                  (c) => !c.isPlatformClient
                ).length;
                return destVisible === destTotal
                  ? `${destTotal} destination clients`
                  : `${destVisible} of ${destTotal} destination clients`;
              })()}
            </p>
          )}
        </div>

        {/* Destination client search */}
        {!loading && clients.length > 0 && (
          <input
            type="text"
            placeholder="Search destination clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
          />
        )}

        {selectedIds.size > 0 && (
          <button
            onClick={() => setShowModal(true)}
            disabled={!selectedPath}
            className="px-4 py-2 bg-black text-white text-sm font-medium rounded-md disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
          >
            Propagate to {selectedIds.size} selected &rarr;
          </button>
        )}
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex flex-1 items-center justify-center gap-x-3 text-sm text-gray-500">
          <LoadingSpinner />
          <span>Loading destination clients&hellip;</span>
        </div>
      ) : error ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 max-w-md">
            {error}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 overflow-hidden">
          {/* Left panel — config tree */}
          <div className="w-64 shrink-0 border-r border-gray-200 flex flex-col overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Config path
              </p>
            </div>
            <div className="flex-1 overflow-hidden">
              <ConfigPathSelector
                config={representativeConfig}
                selectedPath={selectedPath}
                onSelect={setSelectedPath}
              />
            </div>
          </div>

          {/* Right panel — comparison table */}
          <div className="flex-1 overflow-hidden">
            {!selectedPath ? (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Select a config path on the left to compare values.
              </div>
            ) : (
              <ComparisonTable
                clients={filteredClients}
                selectedPath={selectedPath}
                modelClientId={modelClientId}
                onSetModel={handleSetModel}
                selectedIds={selectedIds}
                onToggleSelect={handleToggleSelect}
                onSelectAllDiffering={handleSelectAllDiffering}
                onClearSelection={handleClearSelection}
                onInspect={setInspectingClient}
              />
            )}
          </div>
        </div>
      )}

      {/* Propagation confirmation modal */}
      {showModal && (
        <PropagationModal
          selectedClients={selectedClients}
          selectedPath={selectedPath}
          modelValue={modelValue}
          onConfirm={handlePropagate}
          onClose={() => setShowModal(false)}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Config inspector drawer — view full config of a single destination client */}
      {inspectingClient && (
        <div className="fixed inset-0 z-40 flex justify-end bg-black/30">
          <div className="w-full max-w-lg bg-white shadow-xl flex flex-col overflow-hidden animate-slide-in-right">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div className="min-w-0">
                <h2 className="text-base font-semibold truncate">
                  {inspectingClient.displayName}
                </h2>
                <p className="text-xs text-gray-400 font-mono">
                  {inspectingClient.id}
                </p>
              </div>
              <button
                onClick={() => setInspectingClient(null)}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none ml-4"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {inspectingClient.config ? (
                <pre className="text-xs font-mono text-gray-700 whitespace-pre-wrap break-all">
                  {JSON.stringify(inspectingClient.config, null, 2)}
                </pre>
              ) : (
                <p className="text-sm text-gray-400">No config available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientConfigComparison;
